import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import debounce from "lodash.debounce";

const CountryCard = React.memo(({ country }) => (
  <div className="shadow-md dark:bg-slate-500 rounded-md overflow-hidden">
    <Link to={`/details/${country.name.common}`}>
      <img src={country.flags.svg} alt={country.name.common} className="w-full h-48 object-cover" />
      <div className="p-4">
        <h6 className="font-bold">
          <b>NAME: </b>
          {country.name.common}
        </h6>
        <h6>
          <b>REGION: </b>
          {country.continents[0]}
        </h6>
        <h6>
          <b>TOTAL POP: </b>
          {country.population.toLocaleString()} M
        </h6>
      </div>
    </Link>
  </div>
));

const MainPage = () => {
  const [searchText, setSearchText] = useState("");
  const [countries, setCountries] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const regions = [
    { name: "Filter by region" },
    { name: "Europe" },
    { name: "Asia" },
    { name: "Africa" },
    { name: "Oceania" },
    { name: "Americas" },
    { name: "Antarctic" },
  ];

  const fetchCountries = async () => {
    try {
      setIsLoading(true);
      const { data } = await axios.get("https://restcountries.com/v3.1/all");
      setCountries(data);
      setError(null);
    } catch (err) {
      setError("Failed to fetch countries. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const searchCountry = useCallback(
    debounce(async (term) => {
      if (term.length < 3) return;
      try {
        setIsLoading(true);
        const { data } = await axios.get(`https://restcountries.com/v3.1/name/${term}`);
        setCountries(data);
        setError(null);
      } catch (err) {
        setError("No countries found. Please refine your search.");
      } finally {
        setIsLoading(false);
      }
    }, 500),
    []
  );

  const filterByRegion = async (region) => {
    if (region === "Filter by region") return;
    try {
      setIsLoading(true);
      const { data } = await axios.get(`https://restcountries.com/v3.1/region/${region}`);
      setCountries(data);
      setError(null);
    } catch (err) {
      setError("Failed to filter countries. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchText("");
    fetchCountries();
  };

  useEffect(() => {
    fetchCountries();
    document.title = `Showing All Countries`;
  }, []);

  return (
    <>
      <div className="flex flex-col md:flex-row container mx-auto mb-10 max-w-7xl px-4">
        <form
          onSubmit={(e) => e.preventDefault()}
          autoComplete="off"
          className="flex items-center w-full md:w-1/2 mb-4 md:mb-0"
        >
          <i className="fa fa-search my-auto -mr-9 z-10 pr-2 pl-3 py-5 rounded-md text-gray-400"></i>
          <input
            type="text"
            placeholder="Search for a country..."
            name="search"
            id="search"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              searchCountry(e.target.value);
            }}
            className="p-2 pl-10 shadow-md rounded-md w-full dark:bg-gray-700"
          />
          {searchText && (
            <button
              type="button"
              onClick={clearSearch}
              className="ml-2 px-4 py-2 bg-red-500 text-white rounded-md"
            >
              Clear
            </button>
          )}
        </form>
        <form className="w-full md:w-1/2">
          <select
            name="filter-by-region"
            id="filter-by-region"
            className="w-full py-2 px-4 outline-none shadow rounded text-gray-600 dark:text-gray-400 dark:bg-gray-800 dark:focus:bg-gray-700"
            onChange={(e) => filterByRegion(e.target.value)}
          >
            {regions.map((region, index) => (
              <option key={index} value={region.name}>
                {region.name}
              </option>
            ))}
          </select>
        </form>
      </div>
      <div className="container mx-auto px-4">
        {isLoading ? (
          <h1>Loading...</h1>
        ) : error ? (
          <h1 className="text-red-500">{error}</h1>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {countries.map((country, index) => (
              <CountryCard key={index} country={country} />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default MainPage;
